import { createContext, useContext, useEffect, useState } from 'react'
import { fontOptionMeta, fontOptions, type FontOption } from '@/config/font-options'
import { getCookie, setCookie } from '@/lib/cookies'

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

type FontRolesContextType = {
  headingFont: FontOption
  dataFont: FontOption
  setHeadingFont: (font: FontOption) => void
  setDataFont: (font: FontOption) => void
}

const FontRolesContext = createContext<FontRolesContextType | null>(null)

export function FontRolesProvider({ children }: { children: React.ReactNode }) {
  const [headingFont, _setHeadingFont] = useState<FontOption>(() => {
    const saved = getCookie('font-heading')
    return fontOptions.includes(saved as FontOption) ? (saved as FontOption) : 'lora'
  })

  const [dataFont, _setDataFont] = useState<FontOption>(() => {
    const saved = getCookie('font-data')
    return fontOptions.includes(saved as FontOption) ? (saved as FontOption) : 'jetbrains-mono'
  })

  useEffect(() => {
    document.documentElement.style.setProperty('--font-heading', fontOptionMeta[headingFont].family)
  }, [headingFont])

  useEffect(() => {
    document.documentElement.style.setProperty('--font-data', fontOptionMeta[dataFont].family)
  }, [dataFont])

  const setHeadingFont = (font: FontOption) => {
    setCookie('font-heading', font, COOKIE_MAX_AGE)
    _setHeadingFont(font)
  }

  const setDataFont = (font: FontOption) => {
    setCookie('font-data', font, COOKIE_MAX_AGE)
    _setDataFont(font)
  }

  return (
    <FontRolesContext value={{ headingFont, dataFont, setHeadingFont, setDataFont }}>
      {children}
    </FontRolesContext>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useFontRoles = () => {
  const context = useContext(FontRolesContext)
  if (!context) throw new Error('useFontRoles must be used within a FontRolesProvider')
  return context
}
